import { useState } from "react";
import { NOTIFICATION_CATEGORIES, wantsNotification, type NotificationCategory } from "@starsach/shared-types";
import { useAuth } from "../../auth/AuthProvider";
import { Card, ErrorState } from "../../components/ui";

/**
 * #564: what this person hears about by email. The ticks come from the same
 * defaults table the sender reads (`wantsNotification`), so a box shown
 * ticked is a box that sends. Saved as part of the preferences: audited,
 * and undoable from Security's changes list.
 */
export function NotificationsSection() {
  const { me, preferences, updatePreferences } = useAuth();
  const [saving, setSaving] = useState<NotificationCategory>();
  const [saved, setSaved] = useState<NotificationCategory>();
  const [error, setError] = useState<unknown>();
  const staff = me?.role === "internal-team" && !me.viewAs;

  const toggle = async (category: NotificationCategory, email: boolean) => {
    setSaving(category);
    setSaved(undefined);
    setError(undefined);
    try {
      await updatePreferences({ notifications: { ...preferences.notifications, [category]: { email } } });
      setSaved(category);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(undefined);
    }
  };

  return (
    <div id="notifications" className="scroll-mt-6">
      <Card title="Notifications">
        <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-400">
          Email about the environments you can see. {staff ? "As Gairm staff you start with everything off - the alarms Slack channel is yours - and anything you turn on covers every organization you may open." : "Alarms open a case either way; this is who hears about it by email."}
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 text-left dark:border-neutral-800">
              <th scope="col" className="py-2 pr-4 font-medium">Notification</th>
              <th scope="col" className="py-2 font-medium">Email</th>
            </tr>
          </thead>
          <tbody>
            {NOTIFICATION_CATEGORIES.map((c) => {
              const on = wantsNotification(preferences.notifications, c.id, "email", staff);
              const id = `notify-${c.id}-email`;
              return (
                <tr key={c.id} className="border-b border-neutral-100 align-top dark:border-neutral-900">
                  <td className="py-2 pr-4">
                    <label htmlFor={id} className="font-medium">{c.label}</label>
                    <p className="text-xs text-neutral-500">{c.covers} {c.defaults[staff ? "staff" : "customer"].email ? "On unless you turn it off." : "Off unless you turn it on."}</p>
                  </td>
                  <td className="py-2">
                    <input id={id} type="checkbox" className="h-4 w-4" checked={on} disabled={saving !== undefined || Boolean(me?.viewAs)} onChange={(e) => void toggle(c.id, e.target.checked)} />
                    {saving === c.id && <span className="ml-2 text-xs text-neutral-500">Saving…</span>}
                    {saved === c.id && <span role="status" className="ml-2 text-xs text-green-700 dark:text-green-300">Saved</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-neutral-500">Sign-in codes, invitations and changes to your own account are always sent, whatever you choose here.</p>
        {error !== undefined && <ErrorState error={error} />}
      </Card>
    </div>
  );
}
